/**
 * Campaigns API Client
 * 
 * Handles all campaign-related API calls to the backend.
 * Uses Shopify session tokens for authentication.
 */

import { adminApiRequest } from "./admin-api-client";
import type { WheelConfigV1, WheelSpinResponse } from "../types/wheel";

// ============================================================================
// Types
// ============================================================================

/** v1: optional ISO 8601 instants (UTC). Empty object = no window restriction. */
export interface CampaignScheduleV1 {
  start?: string;
  end?: string;
}

export interface Campaign {
  id: string;
  shopId: string;
  name: string;
  placement: string;
  /** Higher wins when several Active campaigns use the same placement. */
  priority: number;
  status: "DRAFT" | "ACTIVE" | "PAUSED" | "ENDED";
  schedule: CampaignScheduleV1;
  targetingRules: any;
  /** Storefront: modal vs inline (MVP). */
  displayMode?: string;
  triggerType?: string;
  triggerDelayMs?: number | null;
  createdAt: string;
  updatedAt: string;
  variants?: CampaignVariant[];
  _count?: {
    sessions: number;
    events: number;
  };
}

export interface CampaignVariant {
  id: string;
  campaignId: string;
  gameId: string;
  gameVersionId: string;
  rewardPolicyId: string;
  weight: number;
  config: any;
  /** Segments + rewards only (not legacy game config). */
  wheelConfig?: unknown;
  status: "ACTIVE" | "DISABLED";
  createdAt: string;
  updatedAt: string;
  game?: Game;
  gameVersion?: GameVersion;
  rewardPolicy?: RewardPolicy;
}

export interface Game {
  id: string;
  key: string;
  name: string;
  description?: string;
  status: "ACTIVE" | "HIDDEN" | "DEPRECATED";
  tags: string[];
  versions?: GameVersion[];
}

export interface GameVersion {
  id: string;
  gameId: string;
  version: string;
  status: "ACTIVE" | "DEPRECATED";
  manifest: any;
  entryUrl: string;
  assets: any;
  placements: string[];
  configSchema: any;
  releasedAt: string;
}

export interface RewardPolicy {
  id: string;
  shopId: string;
  name: string;
  policy: any;
  expectedValue: number;
  createdAt: string;
  updatedAt: string;
  /** From GET/POST/PUT list/detail; number of campaign variants using this policy. */
  _count?: {
    variants: number;
  };
}

export interface CreateCampaignRequest {
  name: string;
  placement: string;
  /** Integer; higher value is chosen first when multiple Active campaigns share this placement. */
  priority?: number;
  status?: "DRAFT" | "ACTIVE" | "PAUSED";
  schedule?: CampaignScheduleV1;
  targetingRules?: any;
  displayMode?: string;
  triggerType?: string;
  triggerDelayMs?: number | null;
}

export interface UpdateCampaignRequest {
  name?: string;
  placement?: string;
  priority?: number;
  status?: "DRAFT" | "ACTIVE" | "PAUSED" | "ENDED";
  schedule?: CampaignScheduleV1;
  targetingRules?: any;
  displayMode?: string;
  triggerType?: string;
  triggerDelayMs?: number | null;
}

export interface CreateRewardPolicyRequest {
  name: string;
  policy: any;
  expectedValue?: number;
}

export interface UpdateRewardPolicyRequest {
  name?: string;
  policy?: any;
  expectedValue?: number;
}

export interface CreateVariantRequest {
  gameId: string;
  gameVersionId: string;
  rewardPolicyId: string;
  weight?: number;
  config?: any;
  /** Initial wheel config when creating a wheel variant (e.g. catalog template defaults). */
  wheelConfig?: unknown;
  status?: "ACTIVE" | "DISABLED";
}

export interface UpdateVariantRequest {
  weight?: number;
  config?: any;
  wheelConfig?: unknown;
  status?: "ACTIVE" | "DISABLED";
  /** Must reference a reward policy belonging to the same shop. */
  rewardPolicyId?: string;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get all campaigns for the current shop
 */
export async function getCampaigns(): Promise<Campaign[]> {
  const data = await adminApiRequest<{ campaigns: Campaign[] }>("/admin/campaigns");
  return data.campaigns;
}

/**
 * Get single campaign by ID
 */
export async function getCampaign(id: string): Promise<Campaign> {
  const data = await adminApiRequest<{ campaign: Campaign }>(`/admin/campaigns/${id}`);
  return data.campaign;
}

/**
 * Create new campaign
 */
export async function createCampaign(request: CreateCampaignRequest): Promise<Campaign> {
  const data = await adminApiRequest<{ campaign: Campaign }>("/admin/campaigns", {
    method: "POST",
    body: JSON.stringify(request),
  });
  return data.campaign;
}

/**
 * Update existing campaign
 */
export async function updateCampaign(
  id: string,
  request: UpdateCampaignRequest
): Promise<Campaign> {
  const data = await adminApiRequest<{ campaign: Campaign }>(`/admin/campaigns/${id}`, {
    method: "PUT",
    body: JSON.stringify(request),
  });
  return data.campaign;
}

/**
 * Delete campaign (soft delete)
 */
export async function deleteCampaign(id: string): Promise<void> {
  await adminApiRequest<void>(`/admin/campaigns/${id}`, {
    method: "DELETE",
  });
}

/**
 * Get all available games
 */
export async function getGames(): Promise<Game[]> {
  const data = await adminApiRequest<{ games: Game[] }>("/admin/games");
  return data.games;
}

/**
 * Get all reward policies for the current shop
 */
export async function getRewardPolicies(): Promise<RewardPolicy[]> {
  const data = await adminApiRequest<{ policies: RewardPolicy[] }>("/admin/reward-policies");
  return data.policies;
}

/**
 * Create new reward policy
 */
export async function createRewardPolicy(
  request: CreateRewardPolicyRequest
): Promise<RewardPolicy> {
  const data = await adminApiRequest<{ policy: RewardPolicy }>("/admin/reward-policies", {
    method: "POST",
    body: JSON.stringify(request),
  });
  return data.policy;
}

/**
 * Update reward policy
 */
export async function updateRewardPolicy(
  id: string,
  request: UpdateRewardPolicyRequest
): Promise<RewardPolicy> {
  const data = await adminApiRequest<{ policy: RewardPolicy }>(`/admin/reward-policies/${id}`, {
    method: "PUT",
    body: JSON.stringify(request),
  });
  return data.policy;
}

/**
 * Delete a reward policy (only allowed when no variants reference it; server enforces).
 */
export async function deleteRewardPolicy(id: string): Promise<void> {
  await adminApiRequest<void>(`/admin/reward-policies/${id}`, {
    method: "DELETE",
  });
}

/**
 * Create campaign variant
 */
export async function createVariant(
  campaignId: string,
  request: CreateVariantRequest
): Promise<CampaignVariant> {
  const data = await adminApiRequest<{ variant: CampaignVariant }>(
    `/admin/campaigns/${campaignId}/variants`,
    {
      method: "POST",
      body: JSON.stringify(request),
    }
  );
  return data.variant;
}

/**
 * Update campaign variant
 */
export async function updateVariant(
  campaignId: string,
  variantId: string,
  request: UpdateVariantRequest
): Promise<CampaignVariant> {
  const data = await adminApiRequest<{ variant: CampaignVariant }>(
    `/admin/campaigns/${campaignId}/variants/${variantId}`,
    {
      method: "PUT",
      body: JSON.stringify(request),
    }
  );
  return data.variant;
}

/**
 * Delete campaign variant
 */
export async function deleteVariant(campaignId: string, variantId: string): Promise<void> {
  await adminApiRequest<void>(`/admin/campaigns/${campaignId}/variants/${variantId}`, {
    method: "DELETE",
  });
}

// ============================================================================
// Game Preview
// ============================================================================

export interface GamePreviewRequest {
  variantId: string;
  gameKey?: string;
  gameVersion?: string;
  config?: any;
}

export interface GamePreviewResponse {
  manifestUrl: string;
  config: any;
  outcome: {
    outcomeKey: string;
    targetAngle: number;
    totalRotation: number;
    segmentLabel: string;
    segmentColor: string;
    data?: Record<string, unknown>;
  };
}

/**
 * Preview game for a variant
 * 
 * Phase 6.0 Track E: Generate server-authoritative preview outcome
 */
export async function previewGame(request: GamePreviewRequest): Promise<GamePreviewResponse> {
  const data = await adminApiRequest<GamePreviewResponse>("/admin/games/preview", {
    method: "POST",
    body: JSON.stringify(request),
  });
  return data;
}

// ============================================================================
// Variant Config Persistence
// ============================================================================

export interface UpdateVariantConfigRequest {
  config: any; // Game-specific config (e.g., WheelConfig)
}

export interface UpdateVariantConfigResponse {
  config: any; // Normalized config returned from server
}

/**
 * Update variant game configuration
 * 
 * Phase 7.0: Persist wheel config with validation
 * 
 * @param variantId - Variant ID
 * @param config - Game-specific config object
 * @returns Normalized config from server
 */
export async function updateVariantConfig(
  variantId: string,
  config: any
): Promise<any> {
  const data = await adminApiRequest<UpdateVariantConfigResponse>(
    `/admin/variants/${variantId}/config`,
    {
      method: "PATCH",
      body: JSON.stringify({ config }),
    }
  );
  return data.config;
}

/**
 * Persist wheel segments + rewards (validated server-side; not game-engine config).
 */
export async function updateWheelConfig(
  variantId: string,
  wheelConfig: WheelConfigV1
): Promise<WheelConfigV1> {
  const data = await adminApiRequest<{ wheelConfig: WheelConfigV1 }>(
    `/admin/variants/${variantId}/wheel-config`,
    {
      method: "PATCH",
      body: JSON.stringify({ wheelConfig }),
    }
  );
  return data.wheelConfig;
}

/**
 * Merchant preview: weighted spin without session persistence.
 */
export async function spinWheelPreview(variantId: string): Promise<WheelSpinResponse> {
  return adminApiRequest<WheelSpinResponse>(
    `/admin/variants/${variantId}/wheel/spin-preview`,
    {
      method: "POST",
      body: JSON.stringify({}),
    }
  );
}

export interface WheelVariantStats {
  sessions: number;
  spins: number;
  leads: number;
  conversionRate: number | null;
  interactionType: "instant" | "lead_capture" | "instant_discount";
  discountSpins: number;
}

/**
 * Lightweight wheel conversion stats (sessions, spins, leads, conversion %).
 */
export async function fetchWheelVariantStats(variantId: string): Promise<WheelVariantStats> {
  return adminApiRequest<WheelVariantStats>(`/admin/variants/${variantId}/wheel-stats`, {
    method: "GET",
  });
}

// ============================================================================
// Targeting Rule Validation
// ============================================================================

export interface TargetingRuleV1 {
  version: "1";
  rule: any;
}

export interface ValidationResult {
  valid: boolean;
  errors: Array<{ path: string; message: string; code: string }>;
}

/**
 * Validate targeting rule via backend API
 * 
 * Phase 5.1: Server-side targeting rule validation
 */
export async function validateTargetingRule(rule: TargetingRuleV1): Promise<ValidationResult> {
  return adminApiRequest<ValidationResult>("/admin/targeting/validate", {
    method: "POST",
    body: JSON.stringify({ rule }),
  });
}

/**
 * Preview targeting rule evaluation (test against sample data)
 * 
 * Phase 5.1: Preview how many users would match this rule
 */
export async function previewTargetingRule(rule: TargetingRuleV1): Promise<any> {
  return adminApiRequest<any>("/admin/targeting/preview", {
    method: "POST",
    body: JSON.stringify({ rule }),
  });
}
